import React, { useMemo, useState, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Link as MuiLink,
  Breadcrumbs,
  Divider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { ROUTES } from "../../constants";
import {
  meterSchema,
  installSchema,
  removeSchema,
  transferSchema,
} from "./schemas";
import { useMetersManagement } from "./useMetersManagement";
import {
  DEFAULT_FORM_VALUES,
  DEFAULT_INSTALL_VALUES,
  DEFAULT_REMOVE_VALUES,
  DEFAULT_TRANSFER_VALUES,
} from "./constants";
import MeterFormDialog from "./components/MeterFormDialog";
import MeterHistoryDrawer from "./components/MeterHistoryDrawer";
import {
  InstallDialog,
  RemoveDialog,
  TransferDialog,
  RetireConfirmDialog,
} from "./components/MeterActionDialogs";
import { createColumns } from "./config/columnsConfig";
import InfoBox from "./components/InfoBox";
import CustomToolbar from "./components/CustomToolbar";

export default function MetersManagement() {
  // Local state
  const [selectedMeterId, setSelectedMeterId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyMeterId, setHistoryMeterId] = useState(null);
  const [showRetired, setShowRetired] = useState(false);

  const {
    metersQuery,
    wellsQuery,
    meterTypesQuery,
    unitNativesQuery,
    galConversionsQuery,
    useMeterHistoryQuery,
    createOrUpdateMutation,
    retireMutation,
    installMutation,
    removeMutation,
    transferMutation,
  } = useMetersManagement();

  // RHF for main meter form
  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useForm({
    resolver: zodResolver(meterSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  // RHF for install form
  const installForm = useForm({
    resolver: zodResolver(installSchema),
    defaultValues: DEFAULT_INSTALL_VALUES,
  });

  // RHF for remove form
  const removeForm = useForm({
    resolver: zodResolver(removeSchema),
    defaultValues: DEFAULT_REMOVE_VALUES,
  });

  // RHF for transfer form
  const transferForm = useForm({
    resolver: zodResolver(transferSchema),
    defaultValues: DEFAULT_TRANSFER_VALUES,
  });

  // Data
  const {
    data: meters,
    error: errorMeters,
    isLoading: loadingMeters,
  } = metersQuery;

  const { data: wells, isLoading: loadingWells } = wellsQuery;
  const { data: meterTypes, isLoading: loadingMeterTypes } = meterTypesQuery;
  const { data: unitNatives, isLoading: loadingUnitNatives } = unitNativesQuery;
  const { data: galConversions, isLoading: loadingGalConversions } =
    galConversionsQuery;

  // Filter meters based on retired status
  const filteredMeters = useMemo(() => {
    if (!meters) return [];
    return showRetired ? meters : meters.filter((meter) => !meter.retired);
  }, [meters, showRetired]);

  const { data: meterHistory, isLoading: loadingHistory } =
    useMeterHistoryQuery(historyMeterId);

  const selectedMeter = useMemo(() => {
    if (!selectedMeterId || !meters) return null;
    const found = meters.find((m) => m.meter_ndx === selectedMeterId);
    return found;
  }, [selectedMeterId, meters]);

  const historyMeter = useMemo(() => {
    if (!historyMeterId || !meters) return null;
    const found = meters.find((m) => m.meter_ndx === historyMeterId);
    return found;
  }, [historyMeterId, meters]);

  /* -------------------------
     Handlers
  ------------------------- */
  const handleAddMeter = useCallback(() => {
    setIsCreateMode(true);
    reset(DEFAULT_FORM_VALUES);
    setOpenDialog(true);
  }, [reset]);

  const handleEditMeter = useCallback(
    (meterData) => {
      if (!meterData) {
        return;
      }
      setIsCreateMode(false);
      reset({
        meter_ndx: meterData.meter_ndx,
        meter_id: meterData.meter_id || "",
        meter_serial: meterData.meter_serial || "",
        meter_make: meterData.meter_make || "",
        meter_model: meterData.meter_model || "",
        meter_type_ndx: meterData.meter_type_ndx || null,
        unit_native_ndx: meterData.unit_native_ndx || null,
        gal_conversion_ndx: meterData.gal_conversion_ndx || null,
        rollover_num: meterData.rollover_num || "",
        notes: meterData.notes || "",
      });
      setOpenDialog(true);
    },
    [reset]
  );

  const handleCloseDialog = useCallback(
    (_event, reason) => {
      if (reason === "backdropClick") return;
      setOpenDialog(false);
      reset(DEFAULT_FORM_VALUES);
    },
    [reset]
  );

  const onSubmitForm = useCallback(
    async (formData) => {
      return new Promise((resolve, reject) => {
        createOrUpdateMutation.mutate(
          { formData, isCreateMode },
          {
            onSuccess: () => {
              handleCloseDialog();
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    },
    [createOrUpdateMutation, isCreateMode, handleCloseDialog]
  );

  const handleDeleteRequest = useCallback((meterNdx) => {
    if (!meterNdx) {
      return;
    }
    setSelectedMeterId(meterNdx);
    setDeleteDialogOpen(true);
  }, []);

  const confirmRetire = useCallback(() => {
    if (!selectedMeterId) return;
    retireMutation.mutate(selectedMeterId);
    setDeleteDialogOpen(false);
  }, [selectedMeterId, retireMutation]);

  const handleInstall = useCallback(
    (meterData) => {
      if (!meterData || meterData.well_ndx) {
        return;
      }
      installForm.reset(DEFAULT_INSTALL_VALUES);
      setInstallDialogOpen(true);
    },
    [installForm]
  );

  const handleRemove = useCallback(
    (meterData) => {
      if (!meterData || !meterData.well_ndx) {
        return;
      }
      removeForm.reset(DEFAULT_REMOVE_VALUES);
      setRemoveDialogOpen(true);
    },
    [removeForm]
  );

  const handleTransfer = useCallback(
    (meterData) => {
      if (!meterData || !meterData.well_ndx) {
        return;
      }
      transferForm.reset({
        ...DEFAULT_TRANSFER_VALUES,
        from_well_ndx: meterData.well_ndx,
      });
      setTransferDialogOpen(true);
    },
    [transferForm]
  );

  const handleHistory = useCallback((meterNdx) => {
    if (!meterNdx) {
      return;
    }
    setHistoryMeterId(meterNdx);
    setHistoryDrawerOpen(true);
  }, []);

  const onSubmitInstall = useCallback(
    async (formData) => {
      return new Promise((resolve, reject) => {
        installMutation.mutate(
          { meterId: selectedMeterId, payload: formData },
          {
            onSuccess: () => {
              setInstallDialogOpen(false);
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    },
    [installMutation, selectedMeterId]
  );

  const onSubmitRemove = useCallback(
    async (formData) => {
      return new Promise((resolve, reject) => {
        removeMutation.mutate(
          { meterId: selectedMeterId, payload: formData },
          {
            onSuccess: () => {
              setRemoveDialogOpen(false);
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    },
    [removeMutation, selectedMeterId]
  );

  const onSubmitTransfer = useCallback(
    async (formData) => {
      return new Promise((resolve, reject) => {
        transferMutation.mutate(
          { meterId: selectedMeterId, payload: formData },
          {
            onSuccess: () => {
              setTransferDialogOpen(false);
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    },
    [transferMutation, selectedMeterId]
  );

  /* -------------------------
     Columns
  ------------------------- */
  const columns = useMemo(() => {
    return createColumns({
      handleEditMeter,
      handleInstall,
      handleTransfer,
      handleRemove,
      handleHistory,
      handleDeleteRequest,
      setSelectedMeterId,
    });
  }, [
    handleEditMeter,
    handleInstall,
    handleRemove,
    handleTransfer,
    handleHistory,
    handleDeleteRequest,
    setSelectedMeterId,
  ]);

  const loading = useMemo(
    () =>
      loadingMeters ||
      loadingWells ||
      loadingMeterTypes ||
      loadingUnitNatives ||
      loadingGalConversions,
    [
      loadingMeters,
      loadingWells,
      loadingMeterTypes,
      loadingUnitNatives,
      loadingGalConversions,
    ]
  );

  const handleRowSelection = useCallback((newSelection) => {
    setSelectedMeterId(newSelection.length > 0 ? newSelection[0] : null);
  }, []);

  const MemoizedCustomToolbar = useCallback(
    () => (
      <CustomToolbar
        showRetired={showRetired}
        setShowRetired={setShowRetired}
        handleAddMeter={handleAddMeter}
        isSubmitting={isSubmitting}
        loading={loading}
      />
    ),
    [showRetired, setShowRetired, handleAddMeter, isSubmitting, loading]
  );

  if (errorMeters) {
    return <div>Error fetching meters: {String(errorMeters)}</div>;
  }

  return (
    <Box>
      <Helmet title="Meters Management" />

      <Typography variant="h3" gutterBottom display="inline">
        Meters Management
      </Typography>
      <Breadcrumbs sx={{ mt: 4 }} aria-label="Breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Meters Management</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 6 }} />

      <InfoBox />

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredMeters || []}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.meter_ndx}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableMultipleRowSelection
          onRowSelectionModelChange={handleRowSelection}
          rowSelectionModel={selectedMeterId ? [selectedMeterId] : []}
          slots={{ toolbar: MemoizedCustomToolbar }}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-cell:focus": { outline: "none" },
            "& .MuiDataGrid-cell:focus-within": { outline: "none" },
            "& .MuiDataGrid-row:hover": {
              cursor: "default !important",
              backgroundColor: "inherit !important",
            },
          }}
        />
      </Box>

      {/* Create/Edit Dialog */}
      <MeterFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        isCreateMode={isCreateMode}
        control={control}
        handleSubmit={handleSubmit}
        onSubmitForm={onSubmitForm}
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        loading={loading}
        meterTypes={meterTypes}
        unitNatives={unitNatives}
        galConversions={galConversions}
      />

      {/* Install Dialog */}
      <InstallDialog
        open={installDialogOpen}
        onClose={() => setInstallDialogOpen(false)}
        installForm={installForm}
        onSubmitInstall={onSubmitInstall}
        wells={wells}
      />

      {/* Remove Dialog */}
      <RemoveDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        removeForm={removeForm}
        onSubmitRemove={onSubmitRemove}
        selectedMeter={selectedMeter}
      />

      {/* Transfer Dialog */}
      <TransferDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        transferForm={transferForm}
        onSubmitTransfer={onSubmitTransfer}
        selectedMeter={selectedMeter}
        wells={wells}
      />

      {/* Retire/Reactivate Confirmation Dialog */}
      <RetireConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        selectedMeter={selectedMeter}
        confirmRetire={confirmRetire}
      />

      {/* History Drawer */}
      <MeterHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => {
          setHistoryDrawerOpen(false);
          setHistoryMeterId(null);
        }}
        historyMeter={historyMeter}
        meterHistory={meterHistory}
        loadingHistory={loadingHistory}
      />
    </Box>
  );
}
